import { memo, useEffect, useMemo, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { ID } from '@rossum/api-client';
import { ColumnHeaderPaper } from '@rossum/components/data-grid/ColumnHeaderPaper';
import {
  commonDataGridStyles,
  getDatagridBackgroundStyles,
  HEADER_COLUMN_HEIGHT,
  restrictedRowStyles,
  ROW_HEIGHT,
} from '@rossum/components/data-grid/styles';
import { Alert, Stack } from '@rossum/ui/material';
import {
  DataGridPro,
  GridFilterItem,
  GridProSlotsComponent,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@rossum/ui/x-data-grid-pro';
import { UPLOAD_HASH } from '../../constants/values';
import { useDataGridLocaleText } from '../../i18n/useDataGridLocaleText';
import { getExpectedErrorKey } from '../../lib/errorHandlers';
import { asScalar } from '../../lib/url';
import usePrometheusMetrics from '../../metrics/index';
import { useSendMetricsToOTEL } from '../../metrics/useSendMetricsToOTEL';
import { DOCUMENTS_QUERY } from '../../redux/modules/localStorage/actions';
import { safeOrganizationSelector } from '../../redux/modules/organization/selectors';
import {
  canManageQueueSelector,
  isUserAdmin,
  isUserAtLeastManager,
} from '../../redux/modules/user/selectors';
import { metrics } from '../../timers/metrics/metrics';
import { State } from '../../types/state';
import { useFeatureFlag } from '../../unleash/useFeatureFlag';
import {
  allowedPageSizes,
  DEFAULT_PAGE_SIZE,
  useNextCursorStore,
  UsePaginationResponse,
  useRowCount,
} from '../document-list-base/hooks/usePagination';
import { SelectAnnotationParams } from '../document-list-base/supportedAnnotationViews';
import { LevelOptions, ViewOptions } from '../document-list-base/types';
import { useCustomizeTable } from '../document-list-column-config/hooks/useCustomizeTable';
import { useTableConfig } from '../document-list-column-config/hooks/useTableConfig';
import { appendSchemaColumns } from '../document-list-columns/appendSchemaColumns';
import { CustomRow } from '../document-list-columns/components/CustomRow';
import { SCHEMA_FIELDS_QUERY_KEY } from '../document-list-columns/hooks/useFetchSchemaFields';
import {
  MetaColDef,
  SchemaColDef,
  TypedGridColDef,
} from '../document-list-columns/TypedGridColDef';
import { useColumns } from '../document-list-columns/useColumns';
import { useMetaColumns } from '../document-list-columns/useMetaColumns';
import {
  filterSchemaColumns,
  getColumnName,
} from '../document-list-columns/utils';
import { FilterPanel } from '../document-list-filtering/FilterPanel';
import { absoluteMaxSizeStyles } from '../document-upload/components/helpers';
import { Upload } from '../document-upload/components/Upload';
import { useWorkspacesWithQueues } from '../queues/hooks/useWorkspacesWithQueues';
import { SAVED_VIEW_QUERY_NAME } from '../saved-views';
import { createActionsColumn } from './ActionColumn';
import { ActionsColumnHeader } from './components/ActionsColumnHeader';
import { BreadCrumbs } from './components/BreadCrumbs';
import { ColumnHeader } from './components/ColumnHeader';
import { EmptyStates } from './components/EmptyStates';
import { ErrorPage } from './components/ErrorPage';
import { PagePreview } from './components/PagePreview';
import { QueueDetails } from './components/QueueDetails';
import { SearchResults } from './components/SearchResults';
import { ToggleSidebarButton } from './components/ToggleSidebarButton';
import { Toolbar } from './components/Toolbar';
import { ViewSwitch } from './components/ViewSwitch';
import {
  contentWrapperStyles,
  navigationPanelInnerStyles,
  navigationPanelOuterStyles,
} from './constants';
import { CTAButtons } from './cta-actions/CTAButtons';
import { SettingsButton } from './cta-actions/SettingsButton';
import { UploadButton } from './cta-actions/UploadButton';
import { useFetchEmailThreadsCounts } from './emails/hooks/useFetchEmailThreadsCounts';
import {
  getActiveQueue,
  getPageSizeFromQuery,
  TransformedData,
} from './helpers';
import { DashboardQuery } from './hooks/useDashboardQuery';
import { useFetchDashboardData } from './hooks/useFetchDashboardData';
import { useFetchDashboardDatapoints } from './hooks/useFetchDashboardDatapoints';
import { useSafelyParsedFiltering } from './hooks/useSafelyParsedFiltering';
import { useStatusColumns } from './hooks/useStatusColumns';
import { useTimeoutSearchQuery } from './hooks/useTimeoutSearchQuery';
import { LazyLoadDatapoints } from './LazyLoadDatapoints';
import { SelectionPanel } from './selection-panel/SelectionPanel';
import { StatusTabs } from './statuses/StatusTabs';
import { AllDocsAnnotation } from './types';

type Props = {
  paginationProps: UsePaginationResponse;
  handleQueryChange: (newQuery: DashboardQuery) => void;
  handleFilterModelChange: (newFilterItems: GridFilterItem[]) => void;
  handleClearAllFilters: () => void;
  query: DashboardQuery;
  handleSelectAnnotation: (params: SelectAnnotationParams) => void;
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  view: ViewOptions;
  setView: (view: ViewOptions) => void;
  level: LevelOptions | null;
  sortingProps: {
    onSortModelChange: (sortModel: GridSortModel) => void;
    sortModel: GridSortModel;
  };
  onSearchSubmit: (searchValue: string | undefined) => void;
  openQueueModal: () => void;
  activeQueueId: ID | undefined;
  isSidebarEnabled: boolean;
};

const restrictedRowClass = 'restricted-row';

const DocumentsContent = memo(
  ({
    paginationProps,
    handleQueryChange,
    handleFilterModelChange,
    handleClearAllFilters,
    query,
    isSidebarOpen,
    toggleSidebar,
    view,
    setView,
    level,
    sortingProps: { onSortModelChange, sortModel },
    onSearchSubmit,
    openQueueModal,
    handleSelectAnnotation,
    activeQueueId,
    isSidebarEnabled,
  }: Props) => {
    const history = useHistory();
    const intl = useIntl();
    const localeText = useDataGridLocaleText();
    const isAdmin = useSelector(isUserAdmin);
    const isAtLeastManager = useSelector(isUserAtLeastManager);
    const organization = useSelector(safeOrganizationSelector);
    const querySearch = query['search'];

    const [rowForPreview, setRowForPreview] = useState<
      TransformedData | undefined
    >(undefined);

    const {
      workspacesWithQueues,
      queues,
      isLoading: isWorkspacesLoading,
    } = useWorkspacesWithQueues({
      enableQueries: true,
      hideDisabledQueues: true,
    });

    const workspacesWithQueuesWithUploadEnabled = workspacesWithQueues?.map(
      w => ({
        ...w,
        queues: w.queues.filter(q => q.settings.uiUploadEnabled),
      })
    );

    const {
      searchAfter,
      paginationModel,
      handlePaginationModelChange,
      setNextToMap,
      shouldForceSetNextCursor,
    } = paginationProps;

    const activeQueue = getActiveQueue({
      level: query['level'],
      queues,
      queueId: activeQueueId,
    });

    const canManageActiveQueue = useSelector((state: State) =>
      canManageQueueSelector(state, activeQueue?.url)
    );

    const { columns: tableConfigColumns, isLoading: isTableConfigLoading } =
      useTableConfig({
        // for a second before the request to fetch queues is finished tableConfig points to org level even if the user is on queue level. To prevent that we condition orgId to query level
        orgId: query['level'] === 'all' ? (organization?.id ?? 0) : 0,
        queueId: activeQueue?.id ?? 0,
      });

    const { validFilters, invalidFilters, shouldShowParsingError } =
      useSafelyParsedFiltering(query['filtering'] ?? '', tableConfigColumns);

    const statusColumns = useStatusColumns({
      queues,
      existingFilterItems: validFilters,
      activeQueue,
    });

    const visibleStatusColumns = statusColumns.map(col => {
      const savedStatusColumn = tableConfigColumns.find(
        c => getColumnName(c) === col.metaName
      );
      return savedStatusColumn
        ? { ...col, visible: savedStatusColumn.visible }
        : col;
    });

    const { data, isInitialLoading, error } = useFetchDashboardData({
      statusColumns: visibleStatusColumns,
      isTableConfigLoading,
      query: {
        ordering: query['ordering'],
        pageSize: getPageSizeFromQuery({
          query,
          fallbackValue: DEFAULT_PAGE_SIZE,
          allowedPageSizes,
        }),
        searchAfter: asScalar(searchAfter),
      },
      searchQuery: query['search'],
      dataGridFiltering: validFilters,
      level: query['level'],
    });

    const schemaColumns = filterSchemaColumns(tableConfigColumns);

    const { fetchMoreDatapoints, datapointData } = useFetchDashboardDatapoints({
      annotationIds:
        data?.results.flatMap(({ restricted_access, id }) =>
          restricted_access ? [] : id
        ) ?? [],
      schemaColumns,
    });

    const { data: counts } = useFetchEmailThreadsCounts({
      queueId: activeQueueId,
    });

    const rows = useMemo(() => {
      return data?.results ?? [];
    }, [data?.results]);

    const errorState = useMemo(
      () =>
        getExpectedErrorKey({
          error,
          expectedError: {
            key: 'no-access' as const,
            value: 'Object does not exist',
          },
        }),
      [error]
    );

    // Clear localStorage on access denied error to prevent redirect loops
    useEffect(() => {
      if (errorState === 'no-access') {
        localStorage.removeItem(DOCUMENTS_QUERY);
      }
    }, [errorState]);

    const doesUserRequireAccess = queues?.length === 0 && !isAdmin;

    const schemaFieldsAreLoading = !!useIsFetching({
      queryKey: [SCHEMA_FIELDS_QUERY_KEY],
    });

    const [rowSelectionModel, setRowSelectionModel] =
      useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });

    const selectedAnnotations = useMemo(() => {
      return (data?.results ?? []).filter(({ id }) =>
        rowSelectionModel.ids.has(id)
      );
    }, [rowSelectionModel, data?.results]);

    const selectionActive = selectedAnnotations.length > 0;

    const { columns: metaColumns } = useMetaColumns<AllDocsAnnotation>({
      annotationColumn: {},
      statusColumn: {},
      detailsColumn: {
        handleSelectAnnotation,
      },
      relationsColumn: {
        handleSelectAnnotation,
      },
      queueColumn: {
        level: query['level'],
      },
      labelsColumn: {},
      ...(isAtLeastManager ? { trainingColumn: {} } : {}),
      documentColumn: {
        setRowForPreview,
      },
    });

    const {
      columnsAreReady,
      columns: baseColumns,
      columnVisibilityModel,
    } = useColumns<AllDocsAnnotation>({
      statusColumns,
      metaColumns,
      tableColumns: tableConfigColumns,
      isTableConfigLoading,
      activeQueueId: activeQueue?.id ?? null,
    });

    const columns = useMemo<
      (
        | TypedGridColDef<AllDocsAnnotation>
        | MetaColDef<AllDocsAnnotation>
        | SchemaColDef<AllDocsAnnotation>
      )[]
    >(() => {
      // Custom header in order to enable tracking on headers
      const columnsWithHeaders = baseColumns.map(
        (
          column
        ):
          | TypedGridColDef<AllDocsAnnotation>
          | MetaColDef<AllDocsAnnotation>
          | SchemaColDef<AllDocsAnnotation> => ({
          ...column,
          renderHeader: ({ colDef }) => <ColumnHeader colDef={colDef} />,
          // !== false is important since undefined means true
          sortable: !selectionActive && column.sortable !== false,
        })
      );

      // Add actions column (dashboard specific)
      return [
        ...columnsWithHeaders,
        createActionsColumn({
          handleSelectAnnotation,
          header: (
            <ActionsColumnHeader
              columns={columnsWithHeaders.flatMap(c =>
                'columnType' in c ? [c] : []
              )}
              selectionActive={selectionActive}
              activeQueue={activeQueue}
            />
          ),
        }),
      ];
    }, [baseColumns, selectionActive, handleSelectAnnotation, activeQueue]);

    const gridIsLoading =
      isInitialLoading || schemaFieldsAreLoading || isTableConfigLoading;

    useNextCursorStore({
      next: data?.pagination.next,
      gridIsLoading,
      setNextToMap,
      shouldForceSetNextCursor,
    });

    const rowCount = useRowCount(data?.pagination.total);

    const apiRef = useGridApiRef();

    const rowsWithSchemaColumns = useMemo(() => {
      return rows.map(row =>
        appendSchemaColumns(
          row,
          schemaColumns,
          datapointData.content,
          datapointData.results.map(r => r.id)
        )
      );
    }, [datapointData, rows, schemaColumns]);

    const filteredColumns = columns.flatMap(col =>
      'columnType' in col ? [col] : []
    );
    const {
      isDisabled,
      handleColumnOrderChange,
      handleWidthChange,
      pinnedColumns,
    } = useCustomizeTable({
      activeQueue,
      columns: filteredColumns,
    });

    const filterableColumns = useMemo(
      () => columns.filter(column => column.filterable),
      [columns]
    );

    const search = useTimeoutSearchQuery(query['search']);
    const viewSwitchIsVisible = !!activeQueue?.inbox;

    const dashboardIsReadyToUse = !!(
      !gridIsLoading &&
      columnsAreReady &&
      workspacesWithQueues &&
      queues
    );

    const sendMetricsToPrometheusEnabled = useFeatureFlag(
      'send-metrics-to-prometheus'
    );

    const prometheusMetricsEnabled =
      Boolean(organization?.uiSettings.send_metrics_to_prometheus) ||
      sendMetricsToPrometheusEnabled;

    const activeOrganizationUrl = organization?.url;

    if (dashboardIsReadyToUse) {
      metrics.stopAnnotationValidate();
    }

    usePrometheusMetrics(
      dashboardIsReadyToUse,
      {
        metricName: 'frontend_document_list_load_time_seconds',
        description: 'Time to load documet list in seconds',
      },
      prometheusMetricsEnabled,
      activeOrganizationUrl
    );

    useSendMetricsToOTEL({
      timerCondition: dashboardIsReadyToUse,
      status: !dashboardIsReadyToUse
        ? 'loading'
        : errorState
          ? 'error'
          : 'success',
      error,
      event: {
        name: 'dashboard_loaded',
      },
    });

    const onErrorReset = () =>
      handleQueryChange({
        filtering: undefined,
        level: 'all',
        ordering: undefined,
        view: 'documents',
        page: '1',
      });

    const clearAllFiltersAndSavedView = () => {
      handleClearAllFilters();
      handleQueryChange({ saved_view: undefined });
    };

    const renderEmptyState = () => (
      <EmptyStates
        searchQuery={search}
        isEmailView={false}
        shouldCreateQueue={
          !isWorkspacesLoading && workspacesWithQueues?.length === 0
        }
        openQueueModal={openQueueModal}
        queue={activeQueue}
        existingFilterItems={validFilters}
        onClearFilters={clearAllFiltersAndSavedView}
        onErrorReset={errorState !== null ? onErrorReset : undefined}
        uploadEnabled={activeQueue?.settings?.uiUploadEnabled ?? true}
      />
    );

    return doesUserRequireAccess || errorState === 'no-access' ? (
      <>
        <ToggleSidebarButton
          isSidebarOpen={isSidebarOpen}
          onClick={toggleSidebar}
        />
        <ErrorPage
          onReset={() =>
            handleQueryChange({
              filtering: undefined,
              level: 'all',
              ordering: undefined,
              view: 'documents',
            })
          }
        />
      </>
    ) : (
      <Stack data-page-title="document-list" {...contentWrapperStyles}>
        {shouldShowParsingError ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => handleQueryChange({ filtering: undefined })}
          >
            {intl.formatMessage({
              id: 'containers.allDocuments.alert.parsingUrlError',
            })}
          </Alert>
        ) : null}

        <Stack {...navigationPanelOuterStyles}>
          <Stack {...navigationPanelInnerStyles}>
            {isSidebarEnabled && (
              <ToggleSidebarButton
                isSidebarOpen={isSidebarOpen}
                onClick={toggleSidebar}
              />
            )}
            <BreadCrumbs
              activeQueueId={activeQueueId}
              allDocumentsLevelIsActive={level === 'all'}
            />
            {activeQueue ? (
              <>
                {viewSwitchIsVisible && (
                  <ViewSwitch
                    isVisible={level === 'queue'}
                    view={view}
                    setView={setView}
                    counts={counts}
                  />
                )}
                {canManageActiveQueue ? (
                  <SettingsButton queueId={activeQueue.id} view="documents" />
                ) : null}
                <QueueDetails activeQueue={activeQueue} />
              </>
            ) : null}
            <Stack direction="row" sx={{ flex: 1 }} spacing={1} useFlexGap>
              <Toolbar onSearchSubmit={onSearchSubmit} search={querySearch} />
              <UploadButton
                tooltipTitle={
                  activeQueue?.settings?.uiUploadEnabled
                    ? ''
                    : intl.formatMessage({
                        id: 'containers.settings.queues.uploadDisabled',
                      })
                }
                disabled={
                  !(dashboardIsReadyToUse && queues.length > 0) ||
                  (level === 'queue' && !activeQueue?.settings?.uiUploadEnabled)
                }
                onClick={() =>
                  history.replace({ ...history.location, hash: UPLOAD_HASH })
                }
              />
              {activeQueue ? (
                <Stack direction="row" spacing={2}>
                  <CTAButtons
                    documentListQuery={{
                      query: {
                        ordering: query['ordering'],
                        pageSize: DEFAULT_PAGE_SIZE,
                      },
                      searchQuery: query['search'],
                      dataGridFiltering: validFilters,
                    }}
                    activeQueue={activeQueue}
                    existingFilterItems={validFilters}
                    annotations={
                      data?.results.flatMap(
                        ({
                          restricted_access,
                          id,
                          status,
                          url,
                          document,
                          queue,
                          attachmentStatus,
                        }) =>
                          restricted_access
                            ? []
                            : {
                                id,
                                status,
                                url,
                                document,
                                queue,
                                attachmentStatus,
                              }
                      ) ?? []
                    }
                  />
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        </Stack>

        <SelectionPanel
          selectedAnnotations={selectedAnnotations}
          setSelectedAnnotations={setRowSelectionModel}
          queues={queues}
        />

        <Stack gap={2}>
          {activeQueue && (
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                borderBottom: theme => `1px solid ${theme.palette.divider}`,
              }}
            >
              <StatusTabs
                columns={filterableColumns}
                existingFilterItems={validFilters.concat(invalidFilters)}
                handleQueryChange={handleQueryChange}
                activeQueue={activeQueue}
                savedViewId={query[SAVED_VIEW_QUERY_NAME]}
              />
            </Stack>
          )}

          <FilterPanel
            columns={filterableColumns}
            validFilters={validFilters}
            invalidFilters={invalidFilters}
            handleFilterModelChange={handleFilterModelChange}
            handleClearAllFilters={clearAllFiltersAndSavedView}
            // we must wait till we have complete columns definition, filterChips for nonexisting columns are removed otherwise
            isLoading={!columnsAreReady}
            hasActiveQueue={!!activeQueue}
          />

          <SearchResults querySearch={query['search']} />
        </Stack>

        <Upload
          // we use key to reset the selectedQueue state while navigating
          key={activeQueue?.url}
          canUploadToQueue={activeQueue?.settings?.uiUploadEnabled ?? true}
          workspacesWithQueues={workspacesWithQueuesWithUploadEnabled}
          activeQueueUrl={activeQueue?.url}
        >
          <LazyLoadDatapoints
            apiRef={apiRef.current}
            fetchMoreDatapoints={fetchMoreDatapoints}
          />
          <DataGridPro
            disableRowSelectionExcludeModel
            apiRef={apiRef}
            loading={gridIsLoading}
            // data
            columns={columns}
            rows={rowsWithSchemaColumns}
            // localization
            localeText={localeText}
            // selection
            checkboxSelection
            disableRowSelectionOnClick
            onRowSelectionModelChange={newRowSelectionModel => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            disableColumnMenu
            rowSelectionModel={rowSelectionModel}
            // columns visibility
            columnVisibilityModel={columnVisibilityModel}
            pinnedColumns={pinnedColumns}
            // columns width
            onColumnWidthChange={handleWidthChange}
            // columns reordering
            onColumnOrderChange={handleColumnOrderChange}
            disableColumnReorder={isDisabled}
            // pagination
            pageSizeOptions={allowedPageSizes}
            rowCount={rowCount}
            pagination
            paginationMode="server"
            onPaginationModelChange={handlePaginationModelChange}
            paginationModel={paginationModel}
            // sorting
            sortingMode="server"
            onSortModelChange={newSortModel => {
              // Without this, sorting of schema columns disappears after refresh
              // On queue level, the dashboard starts loading as "org level", so you can't trust `columnsAreReady` flag on its own.
              if (newSortModel.length === 0 && !dashboardIsReadyToUse) return;
              onSortModelChange(newSortModel);
            }}
            sortModel={sortModel}
            // slots and styles customization
            slots={{
              // TODO: investigate when noRowsOverlay is shown:
              // docs:https://mui.com/x/api/data-grid/data-grid-pro/
              noResultsOverlay: renderEmptyState,
              noRowsOverlay: renderEmptyState,
              // let's try it again with original checkbox
              // baseCheckbox: FakeCheckbox,
              row: CustomRow as GridProSlotsComponent['row'], // GridProSlotsComponent doesn't accept generics https://v7.mui.com/x/common-concepts/custom-components/#using-additional-props
              columnHeaders: ColumnHeaderPaper,
            }}
            slotProps={{
              columnHeaders: {
                // @ts-expect-error
                elevation: 0,
              },
              loadingOverlay: {
                variant: 'linear-progress',
              },
              row: { 'data-cy': 'document-row' },
              basePagination: {
                'data-cy': 'all-documents-pagination',
                // @ts-expect-error wrong type
                backIconButtonProps: {
                  'data-cy': 'all-documents-pagination-prev-btn',
                },
                nextIconButtonProps: {
                  'data-cy': 'all-documents-pagination-next-btn',
                },
                labelRowsPerPage: intl.formatMessage({
                  id: 'containers.allDocuments.pagination.label',
                }),
                sx: {
                  // hide pagination when there are no rows
                  display: rowCount === 0 ? 'none' : 'inherit',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  // vertically center pagination
                  [`> * p`]: {
                    margin: 0,
                  },
                },
              },
              baseSwitch: {
                // @ts-expect-error
                color: 'secondary',
              },
              baseSelect: { native: false },
              baseSelectOption: {
                // @ts-expect-error sx is not in the type definition but it works,
                sx: {
                  // workaround for making first empty option having the same height as other options
                  '&:after': { content: "' '" },
                },
              },
            }}
            rowHeight={ROW_HEIGHT}
            columnHeaderHeight={HEADER_COLUMN_HEIGHT}
            getRowClassName={params =>
              params.row.restricted_access ? restrictedRowClass : ''
            }
            sx={{
              ...commonDataGridStyles,
              ...absoluteMaxSizeStyles,
              ...restrictedRowStyles,
              ...getDatagridBackgroundStyles('paper'),
            }}
          />
          {rowForPreview && (
            <PagePreview
              pages={rowForPreview.pages}
              originalFileName={rowForPreview.original_file_name ?? undefined}
            />
          )}
        </Upload>
      </Stack>
    );
  }
);

export { DocumentsContent };
